import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu with overlay functionality.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} overlay - The overlay element.
 * @property {string | null} originalBodyOverflow - The original body overflow value.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#createOverlay();
    onDocumentReady(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#restoreBodyScroll();
    this.#removeOverlay();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    overlay: null,
    originalBodyOverflow: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Creates and configures the overlay element
   */
  #createOverlay = () => {
    if (this.#state.overlay) return;

    const overlay = document.createElement('div');
    overlay.className = 'header-menu-overlay';
    overlay.setAttribute('aria-hidden', 'true');

    // Add CSS styles for smooth animation
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      opacity: 0;
      visibility: hidden;
      z-index: 1;
      transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
      pointer-events: none;
    `;

    // Click overlay to close menu
    overlay.addEventListener('click', this.#handleOverlayClick);

    document.body.appendChild(overlay);
    this.#state.overlay = overlay;
  };

  /**
   * Removes the overlay element
   */
  #removeOverlay = () => {
    if (this.#state.overlay) {
      this.#state.overlay.removeEventListener('click', this.#handleOverlayClick);
      this.#state.overlay.remove();
      this.#state.overlay = null;
    }
  };

  /**
   * Handles overlay click to close menu
   */
  #handleOverlayClick = () => {
    if (this.#state.activeItem) {
      this.#deactivate();
    }
  };

  /**
   * Disables body scrolling
   */
  #disableBodyScroll = () => {
    if (this.#state.originalBodyOverflow === null) {
      this.#state.originalBodyOverflow = document.body.style.overflow || '';
      document.body.style.overflow = 'hidden';
    }
  };

  /**
   * Restores body scrolling to its original state
   */
  #restoreBodyScroll = () => {
    if (this.#state.originalBodyOverflow !== null) {
      document.body.style.overflow = this.#state.originalBodyOverflow;
      this.#state.originalBodyOverflow = null;
    }
  };

  /**
   * Shows the overlay with smooth animation
   */
  #showOverlay = () => {
    if (!this.#state.overlay) return;

    const overlay = this.#state.overlay;
    overlay.style.visibility = 'visible';
    overlay.style.pointerEvents = 'auto';

    // Disable body scrolling
    this.#disableBodyScroll();

    // Use requestAnimationFrame to ensure the visibility change is applied before opacity
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
    });
  };

  /**
   * Hides the overlay with smooth animation
   */
  #hideOverlay = () => {
    if (!this.#state.overlay) return;

    const overlay = this.#state.overlay;
    overlay.style.opacity = '0';
    overlay.style.pointerEvents = 'none';

    // Restore body scrolling
    this.#restoreBodyScroll();

    // Hide visibility after transition completes
    setTimeout(() => {
      if (overlay.style.opacity === '0') {
        overlay.style.visibility = 'hidden';
      }
    }, 300); // Match CSS transition duration
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();

    if (this.#state.activeItem) {
      this.#activateHandler(event);
    } else {
      this.#debouncedActivateHandler(event);
    }
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    this.dataset.overflowExpanded = (event.target.slot !== '').toString();

    this.#deactivate();
    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = findSubmenu(item);

    if (!submenu) return;

    this.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);

    // Show overlay when menu is activated
    this.#showOverlay();
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate(event);
    }
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce((event) => {
    this.dataset.overflowExpanded = 'false';

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item != this.#state.activeItem) return;

    this.#deactivate(item);
  }, DEACTIVATE_DELAY);

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    this.style.setProperty('--submenu-height', '0px');

    if (!item) return;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    // Hide overlay when menu is deactivated
    this.#hideOverlay();

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[role="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

class MegaMenuNewsletter extends HTMLElement {
  constructor() {
    super();
    this.submitBtn = this.querySelector('form button');
    this.tempBtn = this.querySelector('.mega-menu-list__newsletter-form--tempBtn');
    if (this.submitBtn && this.submitBtn.hasAttribute('disabled')) {
      this.submitBtn.classList.add('hidden');
      this.tempBtn?.classList.remove('hidden');

      setTimeout(() => {
        document.documentElement.removeAttribute('scroll-lock');
      }, 300);

      setTimeout(() => {
        this.submitBtn?.classList.remove('hidden');
        this.tempBtn?.classList.add('hidden');
        this.submitBtn?.removeAttribute('disabled');
      }, 10000);
    }
  }
}
customElements.define('mega-menu-newsletter', MegaMenuNewsletter);
